import { useState, useCallback, useRef } from 'react';
import supervisorAnalyticsService from '../services/supervisorAnalyticsService';
import i18n from '../i18n';

const useSupervisorAnalytics = () => {
    const [summary, setSummary] = useState(null);
    const [loadingItems, setLoadingItems] = useState([]);
    const [loadingTotal, setLoadingTotal] = useState(0);
    const [unloadingItems, setUnloadingItems] = useState([]);
    const [unloadingTotal, setUnloadingTotal] = useState(0);
    const [leaderboard, setLeaderboard] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);

    const loadingAbortRef = useRef(null);
    const unloadingAbortRef = useRef(null);

    const fetchSummary = useCallback(async (params = {}) => {
        setFetching(true);
        setError(null);
        try {
            const result = await supervisorAnalyticsService.getSummary(params);
            if (result.success) {
                setSummary(result.data);
            } else {
                setError(result.error);
                setSummary(null);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.supervisorAnalytics.fetchSummary'));
        } finally {
            setFetching(false);
        }
    }, []);

    const fetchLoading = useCallback(async (params = {}) => {
        if (loadingAbortRef.current) {
            loadingAbortRef.current.abort();
        }
        const controller = new AbortController();
        loadingAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await supervisorAnalyticsService.getLoading(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setLoadingItems(raw?.items ?? raw?.data ?? (Array.isArray(raw) ? raw : []));
                setLoadingTotal(raw?.total ?? raw?.count ?? (Array.isArray(raw) ? raw.length : 0));
            } else {
                setError(result.error);
                setLoadingItems([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.supervisorAnalytics.fetchLoading'));
        } finally {
            if (loadingAbortRef.current === controller) {
                setFetching(false);
                loadingAbortRef.current = null;
            }
        }
    }, []);

    const fetchUnloading = useCallback(async (params = {}) => {
        if (unloadingAbortRef.current) {
            unloadingAbortRef.current.abort();
        }
        const controller = new AbortController();
        unloadingAbortRef.current = controller;

        setFetching(true);
        setError(null);
        try {
            const result = await supervisorAnalyticsService.getUnloading(params, { signal: controller.signal });
            if (result.isCanceled) return;
            if (result.success) {
                const raw = result.data;
                setUnloadingItems(raw?.items ?? raw?.data ?? (Array.isArray(raw) ? raw : []));
                setUnloadingTotal(raw?.total ?? raw?.count ?? (Array.isArray(raw) ? raw.length : 0));
            } else {
                setError(result.error);
                setUnloadingItems([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.supervisorAnalytics.fetchUnloading'));
        } finally {
            if (unloadingAbortRef.current === controller) {
                setFetching(false);
                unloadingAbortRef.current = null;
            }
        }
    }, []);

    const fetchLeaderboard = useCallback(async (params = {}) => {
        setFetching(true);
        setError(null);
        try {
            const result = await supervisorAnalyticsService.getLeaderboard(params);
            if (result.success) {
                setLeaderboard(result.data?.supervisors ?? []);
            } else {
                setError(result.error);
                setLeaderboard([]);
            }
            return result;
        } catch (err) {
            setError(err.message || i18n.t('hookErrors.supervisorAnalytics.fetchLeaderboard'));
        } finally {
            setFetching(false);
        }
    }, []);

    return {
        summary,
        loadingItems,
        loadingTotal,
        unloadingItems,
        unloadingTotal,
        leaderboard,
        fetching,
        error,
        fetchSummary,
        fetchLoading,
        fetchUnloading,
        fetchLeaderboard,
    };
};

export default useSupervisorAnalytics;
